import React from "react";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { t } from "@client/lib/dictionary";
import type { TracerTelemetry } from "@client/types/tracer";
import { formatMetric, metricLabel } from "@client/utils/formatMetric";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";

interface Props {
  slug: string;
  telemetry: TracerTelemetry | undefined;
  compact?: boolean;
}

const Metric: React.FC<{ label: string; value: string; tone?: "good" | "warn" | "bad" }> = ({ label, value, tone }) => {
  const colorClass =
    tone === "good"
      ? "text-green-600"
      : tone === "warn"
        ? "text-amber-600"
        : tone === "bad"
          ? "text-red-600"
          : "text-foreground";
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xl font-bold tabular-nums ${colorClass}`}>{value}</span>
    </div>
  );
};

export const TracerOverviewCard: React.FC<Props> = ({ slug, telemetry, compact = false }) => {
  const topic = (metric: string) => `zige/tracer-${slug}/${metric}/val`;
  const pvVMeta = useMetricMeta(topic("pv-v"));
  const pvAMeta = useMetricMeta(topic("pv-a"));
  const pvWMeta = useMetricMeta(topic("pv-w"));
  const batteryVMeta = useMetricMeta(topic("battery-v"));
  const batterySocMeta = useMetricMeta(topic("battery-soc"));
  const batteryChargeAMeta = useMetricMeta(topic("battery-charge-a"));
  const batteryChargeWMeta = useMetricMeta(topic("battery-charge-w"));
  const batteryNetAMeta = useMetricMeta(topic("battery-net-a"));
  //const loadVMeta = useMetricMeta(topic("load-v"));
  //const loadAMeta = useMetricMeta(topic("load-a"));
  //const loadWMeta = useMetricMeta(topic("load-w"));
  const batteryTempMeta = useMetricMeta(topic("battery-temp"));
  const heatsinkMeta = useMetricMeta(topic("heatsink-temp"));
  const internalTempMeta = useMetricMeta(topic("internal-temp"));
  const ambientTempMeta = useMetricMeta(topic("ambient-temp"));

  if (!telemetry) {
    return (
      <Card>
        <CardContent className="p-6 text-muted-foreground text-center">Čekám na první telemetrii…</CardContent>
      </Card>
    );
  }

  const socTone: "good" | "warn" | "bad" =
    telemetry.batterySocPercent < 20 ? "bad" : telemetry.batterySocPercent < 50 ? "warn" : "good";
  const heatsinkTone: "good" | "warn" | "bad" =
    telemetry.heatsinkTempC > 75 ? "bad" : telemetry.heatsinkTempC > 60 ? "warn" : "good";

  return (
    <Card>
      {!compact && (
        <CardHeader>
          <CardTitle>{t.tracer.detail.overview}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${compact ? "p-4" : ""}`}>
        <Metric
          label={metricLabel(pvVMeta, t.tracer.metrics.pvV)}
          value={formatMetric(telemetry.pvVoltageV, pvVMeta)}
        />
        <Metric
          label={metricLabel(pvAMeta, t.tracer.metrics.pvA)}
          value={formatMetric(telemetry.pvCurrentA, pvAMeta)}
        />
        <Metric label={metricLabel(pvWMeta, t.tracer.metrics.pvW)} value={formatMetric(telemetry.pvPowerW, pvWMeta)} />
        <Metric
          label={metricLabel(batteryVMeta, t.tracer.metrics.batteryV)}
          value={formatMetric(telemetry.batteryVoltageV, batteryVMeta)}
        />
        <Metric
          label={metricLabel(batterySocMeta, t.tracer.metrics.batterySoc)}
          value={formatMetric(telemetry.batterySocPercent, batterySocMeta)}
          tone={socTone}
        />
        <Metric
          label={metricLabel(batteryChargeAMeta, t.tracer.metrics.batteryChargeA)}
          value={formatMetric(telemetry.batteryChargeCurrentA, batteryChargeAMeta)}
        />
        <Metric
          label={metricLabel(batteryChargeWMeta, t.tracer.metrics.batteryChargeW)}
          value={formatMetric(telemetry.batteryChargePowerW, batteryChargeWMeta)}
        />
        {telemetry.batteryNetCurrentA !== null && (
          <Metric
            label={metricLabel(batteryNetAMeta, t.tracer.metrics.batteryNetA)}
            value={formatMetric(telemetry.batteryNetCurrentA, batteryNetAMeta)}
          />
        )}
        {/** 
        <Metric
          label={metricLabel(loadVMeta, t.tracer.metrics.loadV)}
          value={formatMetric(telemetry.loadVoltageV, loadVMeta)}
        />
        <Metric
          label={metricLabel(loadAMeta, t.tracer.metrics.loadA)}
          value={formatMetric(telemetry.loadCurrentA, loadAMeta)}
        />
        <Metric
          label={metricLabel(loadWMeta, t.tracer.metrics.loadW)}
          value={formatMetric(telemetry.loadPowerW, loadWMeta)}
        />
        */}
        <Metric
          label={metricLabel(batteryTempMeta, t.tracer.metrics.batteryTemp)}
          value={formatMetric(telemetry.batteryTempC, batteryTempMeta)}
        />
        <Metric
          label={metricLabel(heatsinkMeta, t.tracer.metrics.heatsinkTemp)}
          value={formatMetric(telemetry.heatsinkTempC, heatsinkMeta)}
          tone={heatsinkTone}
        />
        <Metric
          label={metricLabel(internalTempMeta, t.tracer.metrics.internalTemp)}
          value={formatMetric(telemetry.internalTempC, internalTempMeta)}
        />
        {telemetry.ambientTempC !== null && (
          <Metric
            label={metricLabel(ambientTempMeta, t.tracer.metrics.ambientTemp)}
            value={formatMetric(telemetry.ambientTempC, ambientTempMeta)}
          />
        )}
      </CardContent>
    </Card>
  );
};
